import { useState } from 'react';

import { useTranslation } from 'react-i18next';

import AddRoundedIcon from '@mui/icons-material/AddRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import { Autocomplete, Box, Chip, IconButton, TextField, Typography } from '@mui/material';

import { useProductSets } from '../../hooks';
import type { ToastSeverity } from '../../lib/useToast';
import type { ProductSetResponse } from '../../model/types';
import { ProductSetEditorDrawer } from './ProductSetEditorDrawer';

const CREATE_NEW_OPTION = { id: '__create_new__', code: '', name: '', kind: 'STATIC' as const, entries: [] };

export interface ProductSetPickerProps {
  value: string | null;
  onChange: (productSetId: string | null) => void;
  disabled?: boolean;
  error?: boolean;
  helperText?: string;
  onResult: (severity: ToastSeverity, message: string) => void;
}

/**
 * Single-select over existing product sets **by name**, with a pinned "+ Create a new set" option
 * that opens the editor drawer and auto-selects the result — the picker used inside condition
 * leaves (`PRODUCT_IN_SET`/`CATEGORY_IN_SET`) and reward params (`FIXED_PRICE`, Phase-3 forms).
 * Nowhere does the merchant see or type a UUID (plan-011 §8.10).
 */
export function ProductSetPicker({
  value,
  onChange,
  disabled = false,
  error = false,
  helperText,
  onResult,
}: ProductSetPickerProps) {
  const { t } = useTranslation('promotions');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const sets = useProductSets({ size: 200 });
  const options = sets.data?.content ?? [];
  const selected = options.find((s) => s.id === value) ?? null;

  return (
    <>
      <Autocomplete<ProductSetResponse, false, false, false>
        disabled={disabled}
        loading={sets.isFetching}
        options={[CREATE_NEW_OPTION as ProductSetResponse, ...options]}
        value={selected}
        getOptionLabel={(option) => (option.id === CREATE_NEW_OPTION.id ? '' : option.name)}
        isOptionEqualToValue={(option, val) => option.id === val.id}
        onChange={(_event, newValue) => {
          if (!newValue) {
            onChange(null);
            return;
          }
          if (newValue.id === CREATE_NEW_OPTION.id) {
            setDrawerOpen(true);
            return;
          }
          onChange(newValue.id);
        }}
        renderOption={(props, option) => {
          const { key, ...optionProps } = props;
          if (option.id === CREATE_NEW_OPTION.id) {
            return (
              <li key={key} {...optionProps}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'primary.main' }}>
                  <AddRoundedIcon fontSize="small" />
                  <Typography variant="body2" color="primary.main">
                    {t('productSetPicker.createNew')}
                  </Typography>
                </Box>
              </li>
            );
          }
          return (
            <li key={key} {...optionProps}>
              <Box>
                <Typography variant="body2">{option.name}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {t(`productSetPicker.kind.${option.kind}`)}
                </Typography>
              </Box>
            </li>
          );
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            label={t('productSetPicker.label')}
            error={error}
            helperText={helperText}
          />
        )}
      />
      {selected ? (
        <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Chip
            size="small"
            variant="outlined"
            label={selected.name}
            onDelete={disabled ? undefined : () => onChange(null)}
          />
          <IconButton
            size="small"
            aria-label={t('productSetPicker.editAria')}
            disabled={disabled}
            onClick={() => setDrawerOpen(true)}
          >
            <EditRoundedIcon fontSize="inherit" />
          </IconButton>
        </Box>
      ) : null}

      <ProductSetEditorDrawer
        open={drawerOpen}
        editing={selected && selected.id !== CREATE_NEW_OPTION.id ? selected : undefined}
        onClose={() => setDrawerOpen(false)}
        onSaved={(set) => onChange(set.id)}
        onResult={onResult}
      />
    </>
  );
}
